<template>
  <div class="Benefits">
    <div class="Benefits-content">
      <div class="Benefits-header">
        <h2 v-if="title" class="Benefits-title">
          {{ title }}
        </h2>
        <p v-if="description" class="Benefits-description">
          {{ description }}
        </p>
      </div>

      <ul class="Benefits-list">
        <li v-for="(benefit, index) of benefits" :key="index" class="Benefits-item">
          <Icon v-if="benefit.icon" :icon="benefit.icon" class="Benefits-icon" />
          <div class="Benefits-itemContent">
            <h3 class="Benefits-itemTitle">
              {{ benefit.title }}
            </h3>
            <p class="Benefits-itemDescription" v-html="benefit.description" />
          </div>
        </li>
      </ul>
    </div>

    <div v-if="images?.length" class="Benefits-image">
      <picture class="Benefits-picture">
        <source
          v-for="(image, index) of images.slice(0, -1)"
          :key="index"
          :srcset="image.src"
          :media="image.media"
        >

        <img
          :src="images[images.length - 1].src"
          :alt="altText ?? title ?? ''"
          class="Benefits-imageElement"
          loading="lazy"
        >
      </picture>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from '../../atoms/Icon/Icon.vue';
import { type BenefitsProps } from './Benefits.model';

defineProps<BenefitsProps>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Benefits {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: vars.$dimensions-d-8;
    align-self: stretch;

    @include responsive.tablet-down {
      flex-direction: column-reverse;
    }

    &-content {
      display: flex;
      padding: vars.$patterns-benefitdisplay-lg-md-padding-top-bottom vars.$patterns-benefitdisplay-lg-md-padding-left-right;
      flex-direction: column;
      align-items: flex-start;
      gap: vars.$dimensions-d-8;
      border-radius: vars.$patterns-benefitdisplay-lg-md-border-radius;
      background: var(--section-surface-color, #{vars.$patterns-benefitdisplay-bg-color});

      @include responsive.desktop {
        min-width: 456px;
      }

      @include responsive.tablet-down {
        width: 100%;
      }
    }

    &-title {
      @include text.heading('h2');
      color: vars.$patterns-benefitdisplay-title-color;
    }

    &-list {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: vars.$dimensions-d-5;
      align-self: stretch;
    }

    &-item {
      display: flex;
      align-items: flex-start;
      gap: vars.$dimensions-d-2;
      align-self: stretch;
    }

    &-icon {
      color: vars.$patterns-benefitdisplay-icon-color;
      transform: translateY(2px); // to perfectly align the icon with the first line of the text, as icons are usually slightly higher than the text baseline.
    }

    &-itemTitle {
      @include text.text('lg', $font-weight: vars.$typography-weight-body);
      color: vars.$patterns-benefitdisplay-benefit-title-color;
    }

    &-itemDescription {
      @include text.text('md');
      color: vars.$patterns-benefitdisplay-benefit-description-color;
    }

    &-image, &-content {
      flex: 0 1 50%;
    }

    &-image, &-picture, &-imageElement {
      border-radius: vars.$patterns-benefitdisplay-img-lg-md-border-radius;

      @include responsive.tablet-down {
        width: 100%;
      }
    }
  }
}
</style>
